import { RootState } from "@/models/RootState.type";
import { AppDispatch } from "@/models/AppDispatch.type";
import { createListenerMiddleware } from "@reduxjs/toolkit";
import {
  createLessons,
  updateResourcesQueue,
  updateSkillMasteryQueue,
} from "../slices";
import { getRosterInfo } from "@/hooks/useRosterInfo";
import { SkillType } from "@/enums/SkillType";

export const addCreateLessonsListenerMiddleware = (
  listenerMiddleware: ReturnType<typeof createListenerMiddleware>
) => {
  listenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
    actionCreator: createLessons,
    effect: async (action: any, listenerApi: any) => {
      try {
        const { weeklyPlan, reports, userData } =
          listenerApi.getState() as RootState;
        if (weeklyPlan.data?.curriculum) {
          const { curriculum } = weeklyPlan.data;
          const { students } = getRosterInfo(reports, userData);

          // dispatch gathering skill resources
          const resourcesQueue = curriculum.tracks.reduce(
            (acc, cur) => {
              cur.units.forEach((u) => {
                u.lessons.forEach((l) => {
                  l.skills.forEach((s) => {
                    if (s.skillId && students.length && s.skillType === SkillType.FOCUS) {
                      if (
                        acc.findIndex(
                          (a) =>
                            a.skillId === s.skillId &&
                            a.studentId === students[0].id
                        ) === -1
                      ) {
                        acc.push({
                          skillId: s.skillId,
                          studentId: students[0].id,
                        });
                      }
                    }
                  });
                });
              });
              return acc;
            },
            [] as { skillId: string; studentId: string }[]
          );
          listenerApi.dispatch(updateResourcesQueue(resourcesQueue));

          // dispatch gathering skill mastery data
          const studentIds = students.reduce(
            (a: string[], c: { id: string }) => [...a, c.id],
            [] as string[]
          );
          const masteryQueue = curriculum.tracks.reduce(
            (acc, cur) => {
              cur.units.forEach((u) => {
                u.lessons.forEach((l) => {
                  const skillIds = l.skills.filter(s => s.skillType === SkillType.FOCUS).reduce(
                    (a, c) => (c.skillId ? [...a, c.skillId] : a),
                    [] as string[]
                  );
                  const { dateFrom, dateTo } = l;
                  const dateFromString = new Date(
                    new Date(dateFrom).setHours(0, 0, 0, 0)
                  )
                    .toISOString()
                    .split("T")[0];
                  const dateToString = new Date(
                    new Date(dateTo).setHours(0, 0, 0, 0)
                  )
                    .toISOString()
                    .split("T")[0];
                  acc.push({
                    lessonId: l.lessonPlanId,
                    skillId: skillIds,
                    studentId: studentIds,
                    dateFrom: dateFromString,
                    dateTo: dateToString,
                  });
                });
              });
              return acc;
            },
            [] as {
              lessonId: string;
              skillId: string[];
              studentId: string[];
              dateTo: string;
              dateFrom: string;
            }[]
          );
          listenerApi.dispatch(updateSkillMasteryQueue(masteryQueue));
        }
      } catch (error) {
        console.error("Error queuing resources and mastery fetches", error);
        // listenerApi.dispatch(setLoading(AsyncLoadingState.ERROR));
      }
    },
  });
};
